// Country Configuration registry (read) for the Platform Ops Console — the authoritative
// per-country config (currencies, legal identifiers, payment methods, registration
// requirements). Served by payroll-service. See docs/country-configuration-and-currency.md.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/payroll/platform/countries`;

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CountrySummary {
  id?: string;
  country_code: string;
  country_name: string;
  version: number;
  status: string;
  default_currency: string;
  supported_currencies: string[];
  locale?: string | null;
  timezone?: string | null;
}

export interface IdentifierConfig {
  internalCode?: string;
  displayLabel?: string;
  description?: string;
  issuingAuthority?: string;
  required?: boolean;
  validationPattern?: string;
  placeholder?: string;
  uniquenessPolicy?: string;
  verificationStatus?: string;
}
export interface OrgType { code: string; label: string }
export interface PaymentMethod { code: string; channel: string; label: string }
export interface MobileMoneyProvider { code: string; label: string }
export interface RequiredDoc { code: string; label: string; required?: boolean }
export interface AddressField { key: string; label: string; required?: boolean }

export interface CountryConfigDetail extends CountrySummary {
  tax_identifier: IdentifierConfig;
  company_identifier?: IdentifierConfig | null;
  vat_identifier?: IdentifierConfig | null;
  organization_types: OrgType[];
  address_schema: { fields?: AddressField[] };
  required_documents: RequiredDoc[];
  payroll_pack_country?: string | null;
  payment_methods: PaymentMethod[];
  banking_requirements?: any;
  mobile_money_providers: MobileMoneyProvider[];
  effective_from: string;
}

export async function listCountries(): Promise<CountrySummary[]> {
  const { data } = await axios.get(BASE, { headers: { ...authHeader() } });
  return data.countries ?? [];
}

export async function getCountry(code: string): Promise<CountryConfigDetail> {
  const { data } = await axios.get(`${BASE}/${code}`, { headers: { ...authHeader() } });
  return data;
}

// --- Authoring (F1b) — by config-version id ----------------------------------
const CFG = `${API_V1}/payroll/platform/country-configs`;
const H = () => ({ headers: { ...authHeader() } });

// The editable content (structured scalars + JSON blobs for the nested configs).
export interface ConfigContent {
  country_name: string;
  default_currency: string;
  supported_currencies: string[];
  locale?: string | null;
  timezone?: string | null;
  tax_identifier: any;
  company_identifier?: any;
  vat_identifier?: any;
  organization_types: any;
  address_schema: any;
  required_documents: any;
  payroll_pack_country?: string | null;
  payment_methods: any;
  banking_requirements?: any;
  mobile_money_providers: any;
  reason?: string;
}

export const getConfigById = (id: string) =>
  axios.get<CountryConfigDetail>(`${CFG}/${id}`, H()).then((r) => r.data);
export const createConfigDraft = (body: { country_code: string } & ConfigContent) =>
  axios.post<{ id: string }>(CFG, body, H()).then((r) => r.data);
export const updateConfigDraft = (id: string, body: ConfigContent) =>
  axios.put(`${CFG}/${id}`, body, H()).then((r) => r.data);
export const submitConfig = (id: string) => axios.post(`${CFG}/${id}/submit`, {}, H()).then((r) => r.data);
export const publishConfig = (id: string, effective_from: string, reason?: string) =>
  axios.post(`${CFG}/${id}/publish`, { effective_from, reason }, H()).then((r) => r.data);
export const rejectConfig = (id: string, reason?: string) =>
  axios.post(`${CFG}/${id}/reject`, { reason }, H()).then((r) => r.data);
export const discardConfig = (id: string) => axios.delete(`${CFG}/${id}`, H()).then((r) => r.data);
